import {
  TouchEventHandler,
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState
} from 'react';

import { debounce, uniqBy } from 'lodash-es';

import { ILocation } from '@rbi-ctg/frontend';
import { IStore } from '@rbi-ctg/store';
import { useFavoriteStores } from 'hooks/favorite-stores';
import usePlaceIdDetails from 'hooks/geolocation/use-place-id-details';
import useEffectOnce from 'hooks/use-effect-once';
import useMap from 'hooks/use-map';
import { bounds } from 'hooks/use-map/utils';
import { useGeolocation } from 'state/geolocation';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { ServiceMode, useOrderContext } from 'state/order';
import { GeolocationPermissionStates, isValidPosition } from 'utils/geolocation';
import { cornersForPointWithDiagonal } from 'utils/geometry';
import { milesBetweenCoordinates } from 'utils/restaurant';
import DEFAULT_SEARCH_DIAMETER_M from 'utils/restaurant/default-search-diameter-meters';
import { routes } from 'utils/routing';

import storeReducer, {
  StoreAction,
  initialState,
  searchNearbyNotGranted,
  setActiveStoreId as setActiveStoreIdAction
} from '../ducks/stores';

import useMarkers from './use-markers';
import useSearchStores from './use-search-stores';
import useStoreLocatorRedirects from './use-store-locator-redirects';

export const sortByDistance = (a: IStore, b: IStore) => {
  if (b?.distanceInMiles && b.distanceInMiles > (a?.distanceInMiles || Infinity)) {
    return -1;
  }
  if (a?.distanceInMiles && a.distanceInMiles > (b?.distanceInMiles || Infinity)) {
    return 1;
  }
  return 0;
};

export const addDistanceFromUserCoordinates = (
  store: IStore,
  userCoordinates: ILocation | null
) => {
  if (!userCoordinates) {
    return store;
  }
  const distanceInMiles = milesBetweenCoordinates(userCoordinates, {
    lat: store.latitude,
    lng: store.longitude
  });
  return {
    ...store,
    distanceInMiles
  };
};

export enum StoreLocatorTabs {
  Nearby,
  Favorites,
  Recent,
  Delivery
}

const setupEvtListeners = (onDragEnd: TouchEventHandler) => ({
  dragend: debounce(onDragEnd, 200)
});

const filterStores = (stores: IStore[], serviceMode: ServiceMode) => {
  switch (serviceMode) {
    case ServiceMode.CATERING_PICKUP:
    case ServiceMode.CATERING_DELIVERY:
      return stores.filter(store => store.hasCatering);
    default:
      return stores;
  }
};

export interface IUseStoreLocator {
  ErrorDialog: React.FunctionComponent;
  Dialog: React.FunctionComponent;
  activeStoreId?: string;
  hasPanned: boolean;
  map: JSX.Element;
  onClickSearch(): void;
  setIsStoreInfoModalOpen(value: boolean): void;
  onSelectPlace(address: string, placeId?: string | undefined): void;
  permission: GeolocationPermissionStates;
  redirectUrl: string;
  searchingNearby: boolean;
  searchingRecent: boolean;
  searchingFavs: boolean;
  setActiveStoreId: React.Dispatch<any>;
  nearbyStoreCardRefs?: {
    [key: string]: React.MutableRefObject<HTMLDivElement | null>;
  };
  nearbyStores?: IStore[];
  favStores?: IStore[];
  recentStores?: IStore[];
  userAddress: string;
  zoom: number;
  resetLocation(): Promise<void>;
  currentTab: StoreLocatorTabs;
  handleTabChange(newValue: StoreLocatorTabs): void;
  showGeoAskInNearbyTabWhenEmpty: boolean;
  displayMapViewTabs: boolean;
  enableStoreLocatorDeliveryTab: boolean;
  enableStoreLocatorRecentTab: boolean;
  showStoreLocatorTabs: boolean;
  listViewEnabled: boolean;
  showListView: boolean;
  toggleStoreLocatorView(): void;
  storeLocatorDispatch: StoreAction;
  currentTabHasError: boolean;
}

interface IUseStoreLocatorProps {
  // Loads position and nearest location on mount, default true
  loadPositionOnMount: boolean;
  // Indicates hook use on store locator page, default true
  onStoreLocator: boolean;
}

interface ICurrentcoords {
  lat?: number;
  lng?: number;
}

const initialCurrentCoords = { lat: undefined, lng: undefined };

// TODO: Consider passing context values in, ie: state/payment/usePayment
// can make it easier and more straightforward to test
const defaultArgs = { loadPositionOnMount: true, onStoreLocator: true };
export default function useStoreLocator({
  loadPositionOnMount,
  onStoreLocator
}: IUseStoreLocatorProps = defaultArgs): IUseStoreLocator {
  const listViewEnabled = useFlag(LaunchDarklyFlag.ENABLE_STORE_LOCATOR_LIST_VIEW);
  const [showListView, setShowListView] = useState(listViewEnabled);
  const mParticle = useMParticleContext();

  // When enabled a geolocation ask is shown in the
  // empty state list for nearby on map/list views
  const showGeoAskInNearbyTabWhenEmpty = useFlag(
    LaunchDarklyFlag.ENABLE_GEOLOCATION_ASK_IN_NEARBY_TAB_EMPTY_STORE_LOCATOR
  );

  const storeDiameterForRestaurantListView =
    useFlag(LaunchDarklyFlag.STORE_DIAMETER_FOR_RESTAURANT_LIST_VIEW) || DEFAULT_SEARCH_DIAMETER_M;

  /** NOTE
   * This hook handles the redirects for /store-locator when
   * - no service mode selected show service mode modal (take-out/delivery). TH skips this modal.
   * - ask for user to share geolocation information if they haven't been asked before
   * - skip service mode [takeout/delivery] ask when query param `?sm=takeout` is present
   */
  useStoreLocatorRedirects();
  const [
    {
      nearbyStores,
      searchingNearby,
      favStores,
      searchingFavs,
      recentStores,
      searchingRecent,
      activeStoreId,
      errorNearby,
      errorFavs,
      errorRecent
    },
    storeLocatorDispatch
  ] = useReducer(storeReducer, initialState);

  const { loadCurrentPosition, permission, setUserCoordinates, userCoordinates } = useGeolocation();
  const { clearServerOrder, serviceMode } = useOrderContext();
  const [userAddress, setUserAddress] = useState('');
  const [hasPanned, setHasPanned] = useState(false);
  const [isStoreInfoModalOpen, setIsStoreInfoModalOpen] = useState(false);
  const { storeLocatorCallbackUrl } = useLocationContext();
  const [currentcoords, setCurrentCoords] = useState<ICurrentcoords>(initialCurrentCoords);

  const { showFavoritesByDefault, favStoresEnabled } = useFavoriteStores();

  const enableStoreLocatorDeliveryTab = Boolean(
    useFlag(LaunchDarklyFlag.ENABLE_STORE_LOCATOR_DELIVERY_TAB)
  );
  const enableStoreLocatorRecentTab = Boolean(
    useFlag(LaunchDarklyFlag.ENABLE_STORE_LOCATOR_RECENT_TAB)
  );

  // Figure out if we can show tabs on the map view
  // - feature flag enabled and at least one tab enabled
  // ADD flags for tabs here as new ones become available
  const showStoreLocatorTabs = [favStoresEnabled, enableStoreLocatorDeliveryTab].some(f => f);
  const displayMapViewTabs = Boolean(useFlag(LaunchDarklyFlag.ENABLE_MAP_VIEW_STORE_TABS));

  // Track current tab for user in list view
  const [currentTab, setTab] = useState<StoreLocatorTabs>(
    showFavoritesByDefault ? StoreLocatorTabs.Favorites : StoreLocatorTabs.Nearby
  );

  // Track whether or not the current tab is in an error state
  const [currentTabHasError, setCurrentTabHasError] = useState(false);

  // update whether or not the current tab has an error
  // check any time the tab itself or any of the tab error states changes
  useEffect(() => {
    let tabHasErr = false;
    switch (currentTab) {
      case StoreLocatorTabs.Nearby:
        if (errorNearby) {
          tabHasErr = true;
        }
        break;
      case StoreLocatorTabs.Favorites:
        if (errorFavs) {
          tabHasErr = true;
        }
        break;
      case StoreLocatorTabs.Recent:
        if (errorRecent) {
          tabHasErr = true;
        }
        break;
      default:
    }
    setCurrentTabHasError(tabHasErr);
  }, [currentTab, errorFavs, errorNearby, errorRecent]);

  const toggleStoreLocatorView = useCallback(() => {
    const selectedMapView = 'Selected map view';
    const selectedListView = 'Selected list view';
    mParticle.logEvent(CustomEventNames.STORE_LOCATOR_TAB, EventTypes.Navigation, {
      'Selected view': showListView ? selectedMapView : selectedListView
    });

    if (showListView) {
      setTab(StoreLocatorTabs.Nearby);
    }
    setShowListView(s => !s);
  }, [mParticle, showListView]);

  const eventListeners = useMemo(
    () =>
      setupEvtListeners(() => {
        setHasPanned(true);
      }),
    []
  );

  useEffectOnce(() => {
    clearServerOrder();
  });

  const {
    center,
    createMarker,
    domRef,
    map,
    markers,
    neLat,
    neLng,
    panTo,
    swLat,
    swLng,
    zoom
  } = useMap(
    userCoordinates && userCoordinates.lat
      ? {
          eventListeners,
          position: userCoordinates
        }
      : { eventListeners }
  );

  const {
    ErrorDialog,
    Dialog,
    searchNearbyRestaurants,
    searchFavRestaurants,
    searchRecentRestaurants
  } = useSearchStores({
    center,
    neLat,
    neLng,
    swLat,
    swLng,
    enableQueryRecentStores: enableStoreLocatorRecentTab,
    storeLocatorDispatch
  });

  /**
   * nearbyStoreCardRefs is used to store the DOM ref for store cards
   *  that also are shown on the map as store icons. When a user
   *  clicks on the store icon on the map the nearbyStores associated
   *  nearbyStoreCardRefs ref is scrolled into view
   */
  const nearbyStoreCardRefs = useMemo(
    () =>
      nearbyStores?.reduce(
        (o, store) => ({
          ...o,
          [store._id]: createRef()
        }),
        {}
      ),
    [nearbyStores]
  );

  const setActiveStoreId = (storeId: string) => {
    storeLocatorDispatch(setActiveStoreIdAction(storeId));
  };

  const filteredNearbyStores = useMemo(
    () =>
      filterStores(nearbyStores ?? [], serviceMode)
        .map(store => addDistanceFromUserCoordinates(store, userCoordinates))
        .sort(sortByDistance),
    [serviceMode, nearbyStores, userCoordinates]
  );

  const filteredFavStores = useMemo(
    () =>
      filterStores(favStores ?? [], serviceMode)
        .map(store => addDistanceFromUserCoordinates(store, userCoordinates))
        .sort(sortByDistance),
    [favStores, serviceMode, userCoordinates]
  );

  const getDetails = usePlaceIdDetails();
  const onSelectPlace = useCallback(
    (address: string, placeId?: string) => {
      setUserAddress(address);
      getDetails(placeId).then(results => {
        if (!results) {
          return;
        }
        const { geometry } = results;
        const location = {
          lat: geometry!.location.lat(),
          lng: geometry!.location.lng()
        };

        // NOTE: when we set new user coordinates there are effects
        // in use-store-locator that respond and perform searches needed
        setUserCoordinates(location);
      });
    },
    [setUserAddress, setUserCoordinates, getDetails]
  );

  const redirectUrl = storeLocatorCallbackUrl || routes.menu;

  // When map view tabs are enabled we want to combine
  // all the stores for the visible tabs for map markers to work
  const storesWithMarkers = displayMapViewTabs
    ? uniqBy([...filteredNearbyStores, ...filteredFavStores], '_id')
    : filteredNearbyStores;

  useMarkers({
    createMarker,
    map,
    markers,
    favStores,
    panTo,
    nearbyStores: storesWithMarkers,
    activeStoreId,
    storeLocatorDispatch,
    onClickMarker: (store: IStore) => {
      if (store._id === activeStoreId) {
        return;
      }
      mParticle.logEvent(CustomEventNames.BUTTON_CLICK, EventTypes.Other, {
        Name: 'Store card event',
        StoreId: store._id
      });
      const searchedLocation = {
        lat: store.latitude,
        lng: store.longitude
      };
      return searchNearbyRestaurants(
        searchedLocation,
        bounds({ location: searchedLocation, zoom, ref: domRef.current })
      );
    }
  });

  /*
   * Search restaurants in list view:
   * - only perform search for nearby when we have coords
   */
  const searchRestaurantsForListView = useCallback(
    coords => {
      // If the user has not granted geolocalisation permission and has not
      // make a manual search (coords === null), make the nearby store an empty array
      if (
        !coords &&
        [GeolocationPermissionStates.DENIED, GeolocationPermissionStates.PROMPT].includes(
          permission
        )
      ) {
        return storeLocatorDispatch(searchNearbyNotGranted());
      }
      // Otherwise, prevent searching nearby stores when the user has not changed it's coords (fix flickering issues)
      if (
        currentcoords?.lat !== coords?.lat &&
        currentcoords?.lng !== coords?.lng &&
        isValidPosition(coords)
      ) {
        setCurrentCoords(coords);
        return searchNearbyRestaurants(
          coords,
          cornersForPointWithDiagonal(coords.lat, coords.lng, storeDiameterForRestaurantListView)
        );
      }
    },
    [currentcoords, permission, searchNearbyRestaurants, storeDiameterForRestaurantListView]
  );

  /*
   * When switching to/from the map we want to switch back to
   * the desired tab based on properties, either nearby or favorites
   *
   * And load that tabs stores if it was the same as previous tab
   */
  const showListViewOldRef = useRef(showListView);
  const currentTabOldRef = useRef(currentTab);
  useEffect(() => {
    if (showListViewOldRef.current === false && showListView) {
      const defaultListViewTab = showFavoritesByDefault
        ? StoreLocatorTabs.Favorites
        : StoreLocatorTabs.Nearby;
      setTab(defaultListViewTab);
    }

    // update refs every call
    showListViewOldRef.current = showListView;
    currentTabOldRef.current = currentTab;
  }, [showFavoritesByDefault, showListView, currentTab]);

  /**
   * Perform map view store searches when:
   * - map is mounted, domRef
   * - user coordinates being updated are valid
   * - user navigates from list view to map view
   * - store info modal is closed
   *
   * NOTE: If not on store locator page e.g onStoreLocator = false, return
   */
  useEffect(() => {
    if (showListView || isStoreInfoModalOpen || !onStoreLocator) {
      return;
    }
    if (domRef.current && isValidPosition(userCoordinates)) {
      searchNearbyRestaurants(
        userCoordinates,
        bounds({ location: userCoordinates, zoom, ref: domRef.current })
      );
    }
  }, [
    userCoordinates,
    showListView,
    isStoreInfoModalOpen,
    domRef,
    zoom,
    onStoreLocator,
    searchNearbyRestaurants
  ]);

  useEffect(() => {
    searchFavRestaurants(userCoordinates);
  }, [searchFavRestaurants, userCoordinates]);

  useEffect(() => {
    searchRecentRestaurants(userCoordinates);
  }, [searchRecentRestaurants, userCoordinates]);

  /*
    Perform list view store searches when:
    - userCoordinates change
    - showListView changes
    - store info modal is closed
    - Not on the store locator page e.g. onStoreLocator = false;
  */
  useEffect(() => {
    if (!onStoreLocator || (showListView && !isStoreInfoModalOpen)) {
      searchRestaurantsForListView(userCoordinates);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCoordinates, showListView, isStoreInfoModalOpen]);

  /*
   * Setting our current position on mount and permission granted updated
   */

  useEffectOnce(() => {
    if (permission === GeolocationPermissionStates.GRANTED && loadPositionOnMount) {
      loadCurrentPosition();
    }
  });

  const onClickSearch = useCallback(() => {
    searchNearbyRestaurants(center, {
      ne: {
        lat: neLat,
        lng: neLng
      },
      sw: {
        lat: swLat,
        lng: swLng
      }
    });
    setHasPanned(false);
  }, [center, neLat, neLng, searchNearbyRestaurants, swLat, swLng]);

  const resetLocation = useCallback(() => {
    const setCenter = (location: ILocation | null) => {
      if (location) {
        panTo(location);
        searchNearbyRestaurants(location, bounds({ location, zoom, ref: domRef.current }));
        setHasPanned(false);
      }
    };
    return loadCurrentPosition()
      .then(geoResult => {
        if (geoResult) {
          setCenter(geoResult);
        }
      })
      .catch(() => {
        // no location permissions, use last location since we have no other option
        setCenter(userCoordinates);
      });
  }, [loadCurrentPosition, panTo, searchNearbyRestaurants, zoom, domRef, userCoordinates]);

  return {
    ErrorDialog,
    Dialog,
    activeStoreId,
    hasPanned,
    map,
    onClickSearch,
    onSelectPlace,
    permission,
    redirectUrl,
    searchingNearby: searchingNearby || !nearbyStores,
    searchingRecent: searchingRecent || !recentStores,
    searchingFavs: searchingFavs || !favStores,
    setActiveStoreId,
    nearbyStoreCardRefs,
    nearbyStores: filteredNearbyStores,
    favStores: filteredFavStores,
    recentStores,
    userAddress,
    zoom,
    resetLocation,
    displayMapViewTabs,
    enableStoreLocatorDeliveryTab,
    enableStoreLocatorRecentTab,
    showStoreLocatorTabs,
    showGeoAskInNearbyTabWhenEmpty,
    listViewEnabled,
    showListView,
    toggleStoreLocatorView,
    currentTab,
    handleTabChange: setTab,
    setIsStoreInfoModalOpen,
    storeLocatorDispatch,
    currentTabHasError
  };
}
